import { ArrowRightIcon, DeleteIcon, StarIcon } from "@chakra-ui/icons";
import {
  Avatar,
  Button,
  HStack,
  Icon,
  IconButton,
  Link,
  Stack,
  StackProps,
  Text,
  VStack,
  chakra,
} from "@chakra-ui/react";
import { KYDPerformerProfile } from "@common/interfaces/KYDEvent";
import { KYDSocialPlatforms } from "@common/interfaces/KYDOrganization";
import {
  DragControls,
  Reorder,
  useDragControls,
  useMotionValue,
} from "framer-motion";
import React, { ReactNode, useCallback } from "react";
import { FaGripVertical, FaRegStar, FaStar } from "react-icons/fa";
import { getIconForSocial } from "../../FeedManager/SocialInput";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { SetTimeButton } from "../../../ui/SetTimeButton";

dayjs.extend(utc);
dayjs.extend(timezone);

const Item = chakra(Reorder.Item);

type Platform = KYDPerformerProfile;

export const PerformerDraggable = ({
  name,
  image,
  readOnly = false,
  id,
  onUpdate,
  platforms = [],
  actions,
  setTime,
  onSetTime,
  canSetTime,
  timezone,
}: {
  readOnly: boolean;
  name: string;
  image: string;
  id: string;
  onUpdate: () => void;
  platforms?: Platform[];
  actions: ReactNode;
  setTime?: string;
  onSetTime?: () => void;
  canSetTime?: boolean;
  timezone?: string;
}) => {
  const dragControls = useDragControls();
  const y = useMotionValue(0);

  const handleMouseDownReorder = useCallback(
    (e: React.PointerEvent, item: any, dc: DragControls) => {
      console.log("mouse down");
      dc.start(e);
      document.addEventListener(
        "mouseup",
        () => {
          console.log("mouse up");
          if (!readOnly) {
            onUpdate();
          }
        },
        { once: true }
      );
    },
    [onUpdate]
  );
  return (
    <Item
      width={"full"}
      id={id}
      value={id}
      key={id}
      dragListener={false}
      style={{ y }}
      dragControls={dragControls}
    >
      <PerformerRow
        name={name}
        id={id}
        image={image}
        platforms={platforms}
        actions={actions}
        dragComponent={
          readOnly ? null : (
            <HStack
              display={{ base: "none", md: "inherit" }}
              cursor={"grab"}
              onPointerDown={(e) => {
                if (!readOnly) {
                  handleMouseDownReorder(e, {}, dragControls);
                }
              }}
              h="50px"
              w={["40px", null, "25px"]}
              justify={"center"}
            >
              <Icon color="gray.400" as={FaGripVertical} />
            </HStack>
          )
        }
        setTime={setTime}
        onSetTime={onSetTime}
        canSetTime={canSetTime}
        timezone={timezone}
      />
    </Item>
  );
};

export const FeatureAction = ({
  name,
  onClick,
  isActive = false,
  isDisabled = false,
}) => (
  <IconButton
    icon={isActive ? <FaStar /> : <FaRegStar />}
    onClick={onClick}
    isDisabled={isDisabled}
    disabled={isDisabled}
    aria-label={`Make ${name} main artist`}
  />
);

export const DeleteAction = ({ name, onClick, isDisabled = false }) => (
  <IconButton
    icon={<DeleteIcon />}
    onClick={onClick}
    isDisabled={isDisabled}
    disabled={isDisabled}
    aria-label={`Delete ${name} from lineup`}
  />
);

export const ConfirmAction = ({ name, onClick, isLoading = false }) => (
  <IconButton
    icon={<ArrowRightIcon />}
    onClick={onClick}
    isLoading={isLoading}
    isDisabled={isLoading}
    aria-label={`Add ${name} from lineup`}
  />
);

const InlineText = chakra.span;

export const PerformerRow = ({
  name,
  image,
  id,
  setTime,
  onSetTime,
  platforms = [],
  dragComponent = <React.Fragment></React.Fragment>,
  actions = [],
  actionsWrapperProps = {},
  small = false,
  canSetTime = false,
  timezone,
}: {
  name: string;
  image: string;
  id: string;
  setTime?: string;
  onSetTime?: () => void;
  platforms?: Platform[];
  dragComponent?: React.ReactNode;
  actions: React.ReactNode;
  actionsWrapperProps?: StackProps;
  small?: boolean;
  canSetTime?: boolean;
  timezone?: string;
}) => {
  return (
    <Stack
      direction={["column", "row"]}
      w="full"
      justifyContent={"space-between"}
      borderRadius={"md"}
      userSelect={"none"}
      alignItems={"center"}
    >
      <HStack
        gap={2}
        maxW={["100%", "80%"]}
        w={["full", "auto"]}
        overflow="hidden"
        textOverflow={"ellipsis"}
        wordBreak={"break-all"}
      >
        {dragComponent}
        <Avatar size={small ? "sm" : "md"} src={image} />
        <VStack w="full" gap={0} alignItems={"flex-start"}>
          <Text
            w="full"
            fontWeight={"bold"}
            userSelect={"none"}
            fontSize={small ? "sm" : "md"}
          >
            {name}
          </Text>
          <HStack gap={1}>
            {platforms.map((p, i) => {
              if (!p || !p.followers) return null;
              const { icon, placeholder } = getIconForSocial(
                p.platform as KYDSocialPlatforms
              );
              return (
                <Link href={p.url} target="_blank" key={`${p.id}-${i}`}>
                  <HStack gap={1}>
                    <Icon fontSize="18px" as={icon} />
                    <InlineText w="full" fontSize="xs">
                      {p.display_followers}
                    </InlineText>
                  </HStack>
                </Link>
              );
            })}
          </HStack>
        </VStack>
      </HStack>
      <HStack
        w="full"
        flexWrap={"wrap"}
        maxW={["full", "40%"]}
        alignItems={"center"}
        justifyContent={["flex-start", "flex-end"]}
        {...actionsWrapperProps}
      >
        {/* Set Time display and button */}
        {canSetTime && (
          <SetTimeButton
            setTime={setTime}
            onSetTime={onSetTime}
            timezone={timezone}
          />
        )}
        {actions}
      </HStack>
    </Stack>
  );
};
